
const FableServiceProviderBase = require('fable-serviceproviderbase');
const JsonDiff = require('json-diff');
const Fs = require('fs');
const Path = require('path');
const IntegrationAdapter = require('headlight-integration-adapter');

/**
 * Headlight Manifest Packaging fable service
 *
 * WARNING: This is a work in progress.
 */
class ManifestPackagingService extends FableServiceProviderBase
{
	/**
	 * @param {import('fable')} pFable - The fable instance
	 * @param {any} [pOptions] - The options for the service
	 * @param {any} [pServiceHash] - The hash for the service instance
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {import('fable') & {
		 *    HeadlightRestClient: import('headlight-rest-client')
		 * 	  PictApplication: any
		 * 	  newManyfest: any
		 * 	  providers: any
		 *    servicesMap: {
		 *      IntegrationAdapter: { [hash: string]: IntegrationAdapter? },
		 *    },
		 *  }} */
		this.fable;
		this.serviceType = 'HeadlightPackaging';

		if (!('HeadlightRestClient' in this.fable.services))
		{
			this.fable.serviceManager.addServiceType('HeadlightRestClient', require(`headlight-rest-client`));
			if (!this.options.hasOwnProperty('HeadlightAPIURL'))
			{
				//TODO: should this be /1.0/ only? is there a web usecase? branching on that?
				this.options.HeadlightAPIURL = 'https://api.qa.headlight.com/1.0/';
			}
			this.fable.serviceManager.instantiateServiceProvider('HeadlightRestClient', this.options);
		}

		this.comprehension = {};
		this.manifestCache = {};
		this.cacheInitialized = false;
	}

	/**
	 * Fetch data from a URI
	 *
	 * @param {string} uri - the URI to fetch
	 * @return {Promise<any>} the fetched data
	 */
	async _get(uri)
	{
		return new Promise((resolve, reject) =>
		{
			this.fable.HeadlightRestClient.getJSON(uri, (error, data) =>
			{
				if (error)
				{
					return reject(error);
				}
				resolve(data);
			});
		});
	}

	/**
	 * Fetch an entity set with filtering
	 *
	 * @param {string} entity - the entity to fetch
	 * @param {string} filter - the filter to apply to the entity request
	 * @return {Promise<any>} the fetched data
	 */
	async _getEntitySetAsync(entity, filter)
	{
		return await new Promise((resolve, reject) => 
		{
			// @ts-ignore
			this.fable.EntityProvider.getEntitySet(entity, filter, (error, result) => 
			{
				if (error)
				{
					return reject(error);
				}
				resolve (result);
			}, null, { NoCount: true });
		});
	}

	/**
	 * Fetch the form manifest, if configured.
	 *
	 * NOTE: Mirrors the logic in report_definitions/global/server_js/mixins/document.pre-render-mixins.js#LoadFormManifest
	 *
	 * @param {any} pRNI - the ReportNamedInstance
	 * @param {(uri: string) => Promise<any>} [getImpl] - alternate implementation of HTTP get
	 *
	 * @return {Promise<any>} the form manifest object, or null
	 */
	async _loadFormManifest(pRNI, getImpl)
	{
		const get = getImpl || this._get.bind(this);
		const reportOptions = pRNI.Options;
		if (typeof reportOptions.FormDefinition === 'object')
		{
			return reportOptions.FormDefinition;
		}
		try
		{
			// Resolve the root ManifestSchema record. New ingestion tooling
			// (BLT / bridge-push) sets Options.FormManifestSchemaHash instead
			// of FormManifestSchemaID — accept both.
			let rootManifest = null;
			if (reportOptions.FormManifestSchemaID > 0)
			{
				rootManifest = await get(`ManifestSchema/${reportOptions.FormManifestSchemaID}`);
			}
			else if (reportOptions.FormManifestSchemaHash)
			{
				// Hash is NOT unique across customers, so prefer the schema
				// whose IDCustomer matches the RNI's IDCustomer.
				const tmpSchemas = await get(`ManifestSchemas/FilteredTo/FBV~Hash~EQ~${encodeURIComponent(reportOptions.FormManifestSchemaHash)}`);
				if (Array.isArray(tmpSchemas) && tmpSchemas.length > 0)
				{
					rootManifest = tmpSchemas.find((pSchema) => pSchema.IDCustomer == pRNI.IDCustomer) || tmpSchemas[0];
				}
				else
				{
					this.fable.log.warn(`No ManifestSchema found for hash ${reportOptions.FormManifestSchemaHash} (RNI ${pRNI.Hash || pRNI.IDReportNamedInstance}).`);
				}
			}

			if (!rootManifest)
			{
				return null;
			}

			// Fetch and merge partials using the resolved schema's ID.
			const manifestPartialJoins = await get(`ManifestSchemaManifestSchemaPartialJoins/FilteredTo/FBV~IDManifestSchema~EQ~${rootManifest.IDManifestSchema}`);
			let manifestPartials = [];
			if (manifestPartialJoins.length > 0)
			{
				const manifestSchemaPartialToOrdinal = {};
				for (const join of manifestPartialJoins)
				{
					manifestSchemaPartialToOrdinal[join.IDManifestSchemaPartial] = join.Ordinal;
				}
				manifestPartials = await get(`ManifestSchemaPartials/FilteredTo/FBL~IDManifestSchemaPartial~INN~${manifestPartialJoins.map(j => j.IDManifestSchemaPartial).join(',')}`);
				manifestPartials.sort((a, b) =>
				{
					return (manifestSchemaPartialToOrdinal[a.IDManifestSchemaPartial] ?? 0) - (manifestSchemaPartialToOrdinal[b.IDManifestSchemaPartial] ?? 0);
				});
			}
			return this._mergeManifests(rootManifest, manifestPartials);
		}
		catch (error)
		{
			this.fable.log.error(`Error loading form manifest: ${error}`);
		}
		return null;
	}

	/**
	 * @param {Record<string, any>} pTargetManifest - The target manifest to merge into.
	 * @param {Record<string, any>} pSourceManifest - The source manifest to merge from.
	 *
	 * @return {Record<string, any>} the merged manifest
	 */
	_simpleManifestMerge(pTargetManifest, pSourceManifest)
	{
		if (!pTargetManifest.Descriptors)
		{
			pTargetManifest.Descriptors = {};
		}
		if (!Array.isArray(pTargetManifest.Sections))
		{
			pTargetManifest.Sections = [];
		}
		if (!pTargetManifest.ReferenceManifests)
		{
			pTargetManifest.ReferenceManifests = {};
		}
		for (const [ descriptorKey, descriptor ] of Object.entries(pSourceManifest?.Descriptors || {}))
		{
			if (!pTargetManifest.Descriptors[descriptorKey])
			{
				pTargetManifest.Descriptors[descriptorKey] = descriptor;
			}
			else
			{
				//TODO: do we need to merge descriptors that repeat?
			}
		}
		for (const section of pSourceManifest?.Sections || [])
		{
			if (!pTargetManifest.Sections.find(s => s.Hash === section.Hash))
			{
				pTargetManifest.Sections.push(section);
			}
			else
			{
				//TODO: do we need to merge sections that repeat?
			}
		}
		for (const [ referenceKey, reference ] of Object.entries(pSourceManifest?.ReferenceManifests || {}))
		{
			if (!pTargetManifest.ReferenceManifests[referenceKey])
			{
				pTargetManifest.ReferenceManifests[referenceKey] = reference;
			}
			else
			{
				//TODO: do we need to merge references that repeat?
			}
		}
		return pTargetManifest;
	}

	/**
	 * @param {any} rootManifest - The root ManifestSchema Database Record to merge into.
	 * @param {any[]} manifestPartials - The ManifestSchemaPartial Database Records to merge.
	 * @return {any} the merged schema
	 */
	_mergeManifests(rootManifest, manifestPartials)
	{
		const manifest = rootManifest.Manifest || {};
		manifest.Source = { ManifestSchemaID: rootManifest.IDManifestSchema };
		if (!manifest.Descriptors)
		{
			manifest.Descriptors = {};
		}
		if (!Array.isArray(manifest.Sections))
		{
			manifest.Sections = [];
		}
		if (!manifest.ReferenceManifests)
		{
			manifest.ReferenceManifests = {};
		}
		for (const partial of manifestPartials)
		{
			this._simpleManifestMerge(manifest, partial.ManifestPartial);
		}
		const manifestDefinition =
		{
			Product: 'HeadlightDynamicForm', //TODO: does this matter? does it do anything?
			DefaultFormManifest: manifest,
		};
		return manifestDefinition;
	}

	foundDashboard(recordset, hash)
	{
		const recordSets = this.fable?.PictApplication?.options?.pict_configuration?.DefaultRecordSetConfigurations || [];
		for (let recordSet of recordSets)
		{
			const r = recordSet.RecordSet;
			if (r == recordset && recordSet.RecordSetDashboardManifests?.includes?.(hash))
			{
				return true;
			}
		}
		return false;
	}

	async dynamicDashboardLoad(recordset, hash)
	{
		if (this.foundDashboard(recordset, hash))
		{
			return true;
		}
		const recordSets = this.fable?.PictApplication?.options?.pict_configuration?.DefaultRecordSetConfigurations || [];
		for (let x = 0; x < recordSets.length; x++)
		{
			let recordSet = recordSets[x];
			const r = recordSet.RecordSet;
			if (r == recordset)
			{
				let activeRecordsetConfig = this.fable.services.PictSectionRecordSet.recordSetProviderConfigurations[r];
				activeRecordsetConfig[`RecordSetFilterURLTemplate-Dashboard-${ hash }`] = `/PSRS/{~D:Record.RecordSet~}/SpecificDashboard/${ hash }/FilteredTo/{~D:Record.FilterString~}`;
				activeRecordsetConfig.RecordSetDashboardManifests = recordSet.RecordSetDashboardManifests ? [hash] : recordSet.RecordSetDashboardManifests.concat([hash]);
			}
		}
		return await this.loadRecordSetSchemasForProduct(null, ['Dashboard'], recordset);
	}

	async getAvailableSpecificDashboards(recordset)
	{
		const manifests = await this._getManifestSchemas(`${ recordset }-Dashboard-`, true);
		const hashes = [];
		for (let m of manifests)
		{
			if (m.Manifest?.Scope && !hashes.includes(m.Manifest.Scope))
			{
				hashes.push(m.Manifest.Scope);
			}
		}
		return hashes;
	}

	constructDashboardNav()
	{
		const recordSets = this.fable?.PictApplication?.options?.pict_configuration?.DefaultRecordSetConfigurations || [];
		const navObj = 
		{
			topNav: '',
			sideNav: []
		};
		for (let recordSet of recordSets)
		{
			const r = recordSet.RecordSet;
			const manifestHashes = recordSet.RecordSetDashboardManifests;
			for (let m of manifestHashes)
			{
				const manifest = this.fable.services.PictSectionRecordSet.manifestDefinitions[m];
				if (!manifest || [`${ r }-List`, `${ r }-Dashboard`].includes(m))
				{
					continue;
				}
				navObj.topNav += /*html*/ `
				<a class="navbar-item" onclick="toggleNavBarHamburgerMenu()" href="#/PSRS/${ r }/SpecificDashboard/${ m }">
					<span class="icon">
						<i class="fas ${ manifest.Icon || 'fa-rectangle-list' }"></i>
					</span>
					<span class="icon-text">${ manifest.TitleTemplate || manifest.Scope || m }</span>
				</a>`;
				navObj.sideNav.push( 
				{
					Hash: m,
					Name: manifest.TitleTemplate || manifest.Scope || m,
					Icon: `fas ${ manifest.Icon || 'fa-rectangle-list' }`,
					Address: `#/PSRS/${ r }/SpecificDashboard/${ m }`,
					PanelSource: null,
				});
			}
		}
		return navObj;
	}

	/**
	 * Loads and overrides all manifest schemas for all recordsets (for a particular product/app).
	 * @param {string} [product] - limits the schema load by product
	 * @param {Array<String>} [actions] - limits the schema load to only specific actions
	 * @param {string} [limitingRecordSet] - limits the schema load to only a single recordset
	 */
	async loadRecordSetSchemasForProduct(product, actions = ['List', 'Dashboard', 'Create', 'View', 'Edit', 'Filter'], limitingRecordSet)
	{
		product = product || this.fable?.PictApplication?.options?.pict_configuration?.Product;
		const recordSets = this.fable?.PictApplication?.options?.pict_configuration?.DefaultRecordSetConfigurations || [];
		const manifestPromises = [];
		for (let x = 0; x < recordSets.length; x++)
		{
			let recordSet = recordSets[x];
			const r = recordSet.RecordSet;
			let activeRecordsetConfig = this.fable.services.PictSectionRecordSet.recordSetProviderConfigurations[r];
			if (limitingRecordSet && limitingRecordSet !== r)
			{
				continue;
			}
			try
			{
				const dynamicDashboardHashes = await this.getAvailableSpecificDashboards(r);
				activeRecordsetConfig.RecordSetDashboardManifests = recordSet.RecordSetDashboardManifests || [];
				for (let d of dynamicDashboardHashes)
				{
					if (!recordSet.RecordSetDashboardManifests.includes(d))
					{
						activeRecordsetConfig.RecordSetDashboardManifests.push(d);
						activeRecordsetConfig[`RecordSetFilterURLTemplate-Dashboard-${ d }`] = `/PSRS/{~D:Record.RecordSet~}/SpecificDashboard/${ d }/FilteredTo/{~D:Record.FilterString~}`;
					}
				}
				const dashboardManifestSet = [...(recordSet.RecordSetDashboardManifests || [])];
				const dashboardManifestFetch = async (d) => 
				{
					let tempManifest = await this.loadRecordManifestSchema(r, 'Dashboard', product, d);
					if (!tempManifest)
					{
						tempManifest = this.fable.PictApplication.options.pict_configuration.Manifests[d];
					}
					if (!tempManifest.Form)
					{
						this.fable.services.PictSectionRecordSet.generateManifestTableCells(tempManifest);
					}
					this.fable.services.PictSectionRecordSet.manifestDefinitions[d] = tempManifest;
					this.fable.services.PictSectionRecordSet.manifests[d] = tempManifest.Form ? tempManifest : this.fable.newManyfest(tempManifest);
				};				
				for (let d of dashboardManifestSet)
				{
					if ([`${ r }-List`, `${ r }-Dashboard`].includes(d))
					{
						continue;
					}
					manifestPromises.push(dashboardManifestFetch(d));
				}
				const actionManifestFetch = async (a) => 
				{
					let tempManifest = await this.loadRecordManifestSchema(r, a, product);
					if (!tempManifest)
					{
						tempManifest = this.fable.PictApplication.options.pict_configuration.Manifests[`${ r }-${ a }`];
					}
					if (['Dashboard', 'List'].includes(a))
					{
						this.fable.services.PictSectionRecordSet.generateManifestTableCells(tempManifest);
					}
					this.fable.services.PictSectionRecordSet.manifestDefinitions[`${ r }-${ a }`] = tempManifest;
					this.fable.services.PictSectionRecordSet.manifests[`${ r }-${ a }`] = !['Dashboard', 'List'].includes(a) ? tempManifest : this.fable.newManyfest(tempManifest);
					if (a == 'Filter' && this.fable?.providers?.[`RSP-Provider-${ r }`]?._FilterSchema && tempManifest)
					{
						this.fable.providers[`RSP-Provider-${ r }`]._FilterSchema = tempManifest.ReplaceBaseFilters ? tempManifest.Descriptors : Object.assign(this.fable.providers[`RSP-Provider-${ r }`]._FilterSchema, tempManifest.Descriptors);
					}
				};
				for (let a of actions)
				{
					manifestPromises.push(actionManifestFetch(a));
				}
			}
			catch (e)
			{
				this.fable.log.error('Failed to get manifest for record set.', e);
			}
		}
		await Promise.all(manifestPromises);
	}

	async loadQuickSummaryManifests(entities, defaults, product)
	{
		product = product || this.fable?.PictApplication?.options?.pict_configuration?.Product;
		const manifestPromises = [];
		const manifestConfig = defaults || {};
		for (const entity of entities)
		{
			try
			{
				const summaryManifestFetch = async () => 
				{
					const tempManifest = await this.loadRecordManifestSchema(entity, 'Summary', product);
					if (tempManifest)
					{
						manifestConfig[entity] = tempManifest;
					}
					return tempManifest;
				};
				manifestPromises.push(summaryManifestFetch());
			}
			catch (e)
			{
				this.fable.log.error('Failed to get manifest for entity.', e);
			}
		}
		await Promise.all(manifestPromises);
		this.fable.services.HeadlightQuickSummaries.applyTypeConfig(manifestConfig);
	}

	async loadPrintableManifests(entities, defaults, product)
	{
		product = product || this.fable?.PictApplication?.options?.pict_configuration?.Product;
		const manifestPromises = [];
		const manifestConfig = defaults || {};
		for (const entity of entities)
		{
			try
			{
				const summaryManifestFetch = async () => 
				{
					const tempManifest = await this.loadRecordManifestSchema(entity, 'Printable', product);
					if (tempManifest)
					{
						manifestConfig[entity] = tempManifest;
					}
					return tempManifest;
				};
				manifestPromises.push(summaryManifestFetch());
			}
			catch (e)
			{
				this.fable.log.error('Failed to get manifest for entity.', e);
			}
		}
		await Promise.all(manifestPromises);
		this.fable.providers.QRCodeProvider.applyTypeConfig(manifestConfig);
	}

	/**
	 * Loads a record manifest schema for a particular action type.
	 * @param {string} recordset - The recordset we are fetching a "RecordManifest" for.
	 * @param {string} verb - The verb/"action" this is for (options include Create/View/Edit/List/Dashboard).
	 * @param {string} [product] - The product we are fetching this manifest for.
	 * @param {string} [specifier] - A further specifier beyond the verb. Main purpose is for multiple types of Dashboard.
	 * @returns {Promise<null|Object>} - returns null or the schema loaded from the DB.
	 */
	async loadRecordManifestSchema(recordset, verb, product, specifier)
	{
		if (!recordset || !verb)
		{
			return null;
		}
		let rootManifest = (await this._getManifestSchemas(`${ product ? `${ product }-` : '' }${ recordset }-${ verb }${ specifier ? `-${ specifier }` : '' }`))?.[0];
		if (!rootManifest)
		{
			if (specifier)
			{
				return await this.loadRecordManifestSchema(recordset, verb, product);
			}
			if (product)
			{
				return await this.loadRecordManifestSchema(recordset, verb, null, specifier);
			}
			if (verb === 'Edit')
			{
				return await this.loadRecordManifestSchema(recordset, 'View');
			}
			return null;
		}
		const manifestSchemaPartialJoins = await this._getManifestSchemaPartialJoins(rootManifest.IDManifestSchema);
		const manifestPartials = manifestSchemaPartialJoins.length > 0 ?
			(await this._getManifestSchemaPartials(manifestSchemaPartialJoins.map((m) => m.IDManifestSchemaPartial))) : [];
		const manifest = this._mergeManifests(rootManifest, manifestPartials);
		return manifest?.DefaultFormManifest;
	}

	async primeManifestCache(product, entities)
	{
		if (this.cacheInitialized)
		{
			return;
		}
		product = product || this.fable?.PictApplication?.options?.pict_configuration?.Product;
		const tempCache = {
			ManifestSchema: {},
			ManifestSchemaPartialJoin: {},
			ManifestSchemaPartial: {}
		};
		let schemaArr = [];
		// If a set of entities is passed, limit the set of customer level entities to cache for.
		if (entities)
		{
			const filters = ['FBV~Type~EQ~RecordManifest'];
			// Get all for the product
			if (product)
			{
				filters.push(`FBV~Type~EQ~RecordManifest~FBV~SubType~LK~${ product }-%25`);
			}
			// Get any customer level ones limited by entity.
			for (let e of entities)
			{
				filters.push(`FBVOR~Type~EQ~RecordManifest~FBV~SubType~LK~${ e }-%25`);
			}
			schemaArr = await this._getEntitySetAsync('ManifestSchema', filters.join('~'));
		}
		else
		{
			schemaArr = await this._getEntitySetAsync('ManifestSchema', 'FBV~Type~EQ~RecordManifest');
		}

		for (let s of schemaArr)
		{
			tempCache.ManifestSchema[s.SubType] = s;
		}
		const manifestSchemaPartialJoins = await this._getEntitySetAsync('ManifestSchemaManifestSchemaPartialJoin', `FBL~IDManifestSchema~INN~${ schemaArr.map(s => s.IDManifestSchema).join(',') }`);
		for (let mspj of manifestSchemaPartialJoins)
		{
			tempCache.ManifestSchemaPartialJoin[mspj.IDManifestSchema] = tempCache.ManifestSchemaPartialJoin[mspj.IDManifestSchema] || [];
			tempCache.ManifestSchemaPartialJoin[mspj.IDManifestSchema].push(mspj);
		}
		const manifestPartials = manifestSchemaPartialJoins.length > 0 ?
			(await this._getEntitySetAsync('ManifestSchemaPartial', `FBL~IDManifestSchemaPartial~INN~${ manifestSchemaPartialJoins.map((m) => m.IDManifestSchemaPartial).join(',') }`)) : [];
		for (let msp of manifestPartials)
		{
			tempCache.ManifestSchemaPartial[msp.IDManifestSchemaPartial] = msp;
		}
		this.manifestCache = tempCache
		this.cacheInitialized = true;
	}

	async _getManifestSchemas(subType, partialMatch)
	{
		if (!this.cacheInitialized)
		{
			return await this._getEntitySetAsync('ManifestSchema', `FBV~Type~EQ~RecordManifest~${partialMatch ? `FBV~SubType~LK~%25${ subType }%25` : `FBV~SubType~EQ~${ subType }` }`);
		}
		if (partialMatch)
		{
			const output = [];
			for (let m of Object.keys(this.manifestCache.ManifestSchema))
			{
				if (m.includes(subType))
				{
					output.push(this.manifestCache.ManifestSchema[m]);
				}
			}
			return output;
		}
		return this.manifestCache.ManifestSchema[subType] ? [this.manifestCache.ManifestSchema[subType]] : [];
	}

	async _getManifestSchemaPartials(IDManifestSchemaPartials)
	{
		if (!this.cacheInitialized)
		{
			return await this._getEntitySetAsync('ManifestSchemaPartial', `FBL~IDManifestSchemaPartial~INN~${ IDManifestSchemaPartials.join(',') }`);
		}
		let output = [];
		for (let id of IDManifestSchemaPartials)
		{
			if (this.manifestCache.ManifestSchemaPartial[id])
			{
				output.push(this.manifestCache.ManifestSchemaPartial[id]);
			}
		}
		return output;
	}

	async _getManifestSchemaPartialJoins(IDManifestSchema)
	{
		if (!this.cacheInitialized)
		{
			return await this._getEntitySetAsync('ManifestSchemaManifestSchemaPartialJoin', `FBV~IDManifestSchema~EQ~${ IDManifestSchema }`);
		}
		if (this.manifestCache.ManifestSchemaPartialJoin[IDManifestSchema])
		{
			return this.manifestCache.ManifestSchemaPartialJoin[IDManifestSchema];
		}
		return [];
	}

	async validate()
	{
		const rnis = await this._get(`ReportNamedInstancesByProject/9898/0/250`);
		for (const rni of rnis)
		{
			const fullManifest = rni.Options.FormDefinition;
			if (!fullManifest)
			{
				continue;
			}
			const newPipelineManifest = await this._loadFormManifest(rni);
			const baseSchema = await this._get(`ManifestSchemas/By/Hash/${rni.Hash.toLocaleLowerCase()}`).then((schemas) => schemas[0]);
			if (!baseSchema)
			{
				this.fable.log.info(`ERROR: No base schema for ${rni.Hash}`);
				continue;
			}
			delete rni.Options.FormDefinition;
			rni.Options.FormManifestSchemaID = baseSchema.IDManifestSchema;
			const newPipelineMergedManifest = await this._loadFormManifest(rni, (uri) => this._get(uri));
			const diff = JsonDiff.diffString(newPipelineManifest, newPipelineMergedManifest);
			this.fable.log.info(`Diff for ${rni.Hash}: ${diff}`);
			/*
			const newManifestString = JSON.stringify(newPipelineManifest, null, 2);
			const newMergedManifestString = JSON.stringify(newPipelineMergedManifest, null, 2);
			const fullManifestString = JSON.stringify(fullManifest, null, 2);
			if (newManifestString !== fullManifestString)
			{
				this.fable.log.info(`Mismatch for ${rni.Hash}`);
				this.fable.log.info(`New: ${newManifestString}`);
				this.fable.log.info(`Old: ${fullManifestString}`);
			}
			if (newManifestString !== newMergedManifestString)
			{
				this.fable.log.info(`Mismatch for ${rni.Hash}`);
				this.fable.log.info(`New: ${newManifestString}`);
				this.fable.log.info(`Merged: ${newMergedManifestString}`);
			}
			*/
		}
	}

	/**
	 * @param {string} form - the form name
	 * @param {string} descriptorKey - the descriptor key
	 * @param {any} descriptor - the descriptor object
	 * @param {string} [context] - the context - ie. which manifest or sub-manifest this descriptor is from
	 */
	sanityCheckDescriptor(form, descriptorKey, descriptor, context = 'Root Manifest')
	{
		let result = false;
		if (descriptorKey.includes(' ') || descriptorKey.includes('\t'))
		{
			this.fable.log.info(`[WARN] Form [${form}] [${context}] Descriptor [${descriptorKey}] has spaces in key.`);
			result = true;
		}

		if (!descriptor.Hash || typeof descriptor.Hash !== 'string')
		{
			this.fable.log.info(`[WARN] Form [${form}] [${context}] Descriptor [${descriptorKey}] has no Hash.`, { descriptor });
			result = true;
		}
		else if (descriptor.Hash.includes('.') || descriptor.Hash.includes(' '))
		{
			this.fable.log.info(`[WARN] Form [${form}] [${context}] Descriptor [${descriptorKey}] has Hash with illegal character.`, { descriptor });
			result = true;
		}

		if (Array.isArray(descriptor.PictForm?.SelectOptions))
		{
			const options = descriptor.PictForm.SelectOptions;
			const optionCount = options.length;
			const uniqueOptions = new Set(options.map((o) => o.id));
			if (uniqueOptions.size !== optionCount)
			{
				this.fable.log.info(`[WARN] Form [${form}] [${context}] Descriptor [${descriptorKey}] has duplicate SelectOptions ids.`, { options });
				result = true;
			}
			for (const option of options)
			{
				if (!option.id)
				{
					this.fable.log.info(`[WARN] Form [${form}] [${context}] Descriptor [${descriptorKey}] has SelectOption without id.`, { option });
					result = true;
				}
				if (!option.text)
				{
					this.fable.log.info(`[WARN] Form [${form}] [${context}] Descriptor [${descriptorKey}] has SelectOption without text.`, { option });
					result = true;
				}
				if (option.id == option.text && option.id.includes(' '))
				{
					this.fable.log.info(`[WARN] Form [${form}] [${context}] Descriptor [${descriptorKey}] has SelectOption with probably overspecified id`, { option });
					result = true;
				}
			}
			/* I used this to print all the select lists for Victoria
			this.fable.log.info(`DESCRIPTOR: Form [${form}] [${context}] Descriptor [${descriptorKey}] has ${optionCount} SelectOptions:`);
			for (const option of options)
			{
				this.fable.log.info(`DESCRIPTOR: ${option.id}: ${option.text}`);
			}
			*/
		}
		return result;
	}

	/**
	 * @param {any} manifest - the manifest to check
	 * @param {string} name - the name of the manifest
	 */
	checkManifestForAddressConflicts(manifest, name)
	{
		let result = false;
		const addresses = new Set();
		for (const [ key, descriptor ] of Object.entries(manifest.Descriptors || { }))
		{
			if (addresses.has(key))
			{
				this.fable.log.info(`[WARN] ${name} has duplicate DataAddress [${key}].`, { key, descriptor });
				result = true;
			}
			const sliced = key.split('.');
			while (sliced.length > 1)
			{
				sliced.pop();
				const prefix = sliced.join('.');
				if (addresses.has(prefix))
				{
					const conflict = manifest.Descriptors[prefix];
					this.fable.log.info(`[WARN] ${name} has conflicting DataAddress [${key}] with descriptor at [${prefix}].`, { key, descriptor, conflict });
					result = true;
				}
			}
			addresses.add(key);
		}
		return result;
	}

	/**
	 * @param {string} file - the file path
	 * @return {string} the guessed form name
	 */
	_guessFormName(file)
	{
		const candidates = Path.dirname(file).split(Path.sep);
		while (candidates.length > 0)
		{
			const candidate = candidates.pop();
			if (candidate !== 'source' && candidate.toLowerCase() != 'tmp')
			{
				return candidate;
			}
		}
		return null;
	}

	/**
	 * @param {string} globPath - the glob path to search
	 * @return {Array<ManifestInfo>} the manifest info
	findManifests(globPath)
	{
		const paths = [];
		for (const file of Glob.globSync(globPath, { absolute: true }))
		{
			this.fable.log.info(`Found candidate manifest: ${file}`);
			const wrproducterManifestData = require(file);
			const data = wrproducterManifestData.DefaultFormManifest;
			if (!data)
			{
				this.fable.log.info(`[WARN] No DefaultFormManifest in [${file}]; skipping.`);
				continue;
			}
			paths.push({ path: file, name: this._guessFormName(file), data }) || wrproducterManifestData.DefaultFormManifest.Form || 'uknknown-form';
		}
		return paths;
	}
	 */

	/**
	 * @param {ManifestInfo} manifestInfo - the manifest info
	 * @param {any} comprehension - the comprehension object
	 * @return {boolean} true if problems were found, otherwise false
	 */
	addManifestToComprehension(manifestInfo, comprehension)
	{
		const manifestSchema =
		{
			GUIDManifestSchema: manifestInfo.name,
			Hash: manifestInfo.name,
			Type: 'Form',
			Title: manifestInfo.name,
			Version: 1, //FIXME: version awareness - and Version in GUID
			Description: '',
			Manifest:
			{
				Descriptors: {},
			},
		};
		// copy over any key that isn't part of the three processed by this script
		for (const manifestKey of Object.keys(manifestInfo.data))
		{
			if (manifestKey === 'Descriptors' || manifestKey === 'Sections' || manifestKey === 'ReferenceManifests')
			{
				continue;
			}
			manifestSchema.Manifest[manifestKey] = manifestInfo.data[manifestKey];
		}
		// new manifest per section
		// split the desceriptors by section
		// split the ReferenceManifests by <tk logic>
		const referenceManifestMap = {};
		const seenManifests = new Set();
		const descriptorSectionKeys = new Set();
		const descriptorMap = {};
		const declarationDescriptors = []; // contains descriptors without a section - they are presumed to be declarations
		const declarationDescriptorKeys = new Set();
		const fieldDescriptorKeys = new Set();
		const sectionKeys = new Set();
		const orderedSectionHashes = [];
		const includeBySection = {};
		if (Array.isArray(manifestInfo.data.Sections))
		{
			for (const section of manifestInfo.data.Sections)
			{
				if (typeof section.Hash === 'string')
				{
					if (!sectionKeys.has(section.Hash))
					{
						orderedSectionHashes.push(section.Hash);
						sectionKeys.add(section.Hash);
					}
				}
			}
		}
		let problems = this.checkManifestForAddressConflicts(manifestInfo.data, 'Root Manifest');
		for (const [ descriptorKey, descriptor ] of Object.entries(manifestInfo.data.Descriptors))
		{
			if (descriptorKey !== descriptor.DataAddress)
			{
				this.fable.log.info(`[WARN] Descriptor hash mismatch [${descriptorKey} != ${descriptor.DataAddress}].`, { descriptor });
			}

			descriptorMap[descriptorKey] = descriptor;

			this.sanityCheckDescriptor(manifestInfo.name, descriptorKey, descriptor);

			const sectionHash = descriptor.PictForm?.Section;
			if (typeof sectionHash !== 'string')
			{
				declarationDescriptors.push(descriptor);
				declarationDescriptorKeys.add(descriptorKey);
				continue;
			}
			descriptorSectionKeys.add(sectionHash);
			fieldDescriptorKeys.add(descriptorKey);

			if (!includeBySection[sectionHash])
			{
				includeBySection[sectionHash] = {};
			}
			if (!includeBySection[sectionHash].Descriptors)
			{
				includeBySection[sectionHash].Descriptors = {};
			}
			includeBySection[sectionHash].Descriptors[descriptorKey] = descriptor;
		}
		if (manifestInfo.data.ReferenceManifests)
		{
			for (const [ key, manifest ] of Object.entries(manifestInfo.data.ReferenceManifests))
			{
				problems = this.checkManifestForAddressConflicts(manifestInfo.data, key) || problems;
				referenceManifestMap[key] = manifest;
				for (const [ descriptorKey, descriptor ] of Object.entries(manifest.Descriptors || { }))
				{
					this.sanityCheckDescriptor(manifestInfo.name, descriptorKey, descriptor, `Reference Manifest ${key}`);
				}
			}
		}
		const allSolvers = {};
		const orphanedDeclarationDescriptorHashes = new Set(declarationDescriptorKeys);
		if (Array.isArray(manifestInfo.data.Sections))
		{
			for (const section of manifestInfo.data.Sections)
			{
				if (!section.Hash)
				{
					this.fable.log.info(`[ERROR] No hash for section; skipping.`, { section });
					continue;
				}
				if (!sectionKeys.has(section.Hash))
				{
					orderedSectionHashes.push(section.Hash);
					sectionKeys.add(section.Hash);
				}

				let recordSetCount = 0;
				if (Array.isArray(section.Groups))
				{
					for (const group of section.Groups)
					{
						if (group.RecordSetAddress)
						{
							if (declarationDescriptorKeys.has(group.RecordSetAddress))
							{
								if (!includeBySection[section.Hash])
								{
									includeBySection[section.Hash] = {};
								}
								if (!includeBySection[section.Hash].Descriptors)
								{
									includeBySection[section.Hash].Descriptors = {};
								}
								includeBySection[section.Hash].Descriptors[group.RecordSetAddress] = descriptorMap[group.RecordSetAddress];
								++recordSetCount;
								orphanedDeclarationDescriptorHashes.delete(group.RecordSetAddress);
							}
							else if (!fieldDescriptorKeys.has(group.RecordSetAddress))
							{
								this.fable.log.info(`[WARN] Section [${section.Hash}] Group [${group.Hash || group.Name || '--N/A--'}] RecordSetAddress [${group.RecordSetAddress}] has no matching declaration.`, { group });
							}
						}

						if (group.RecordManifest)
						{
							const referenceManifest = referenceManifestMap[group.RecordManifest];
							if (referenceManifest)
							{
								seenManifests.add(group.RecordManifest);

								if (!includeBySection[section.Hash])
								{
									includeBySection[section.Hash] = {};
								}
								if (!includeBySection[section.Hash].ReferenceManifests)
								{
									includeBySection[section.Hash].ReferenceManifests = {};
								}
								includeBySection[section.Hash].ReferenceManifests[group.RecordManifest] = referenceManifest;
							}
							else
							{
								this.fable.log.info(`[WARN] Section [${section.Hash}] Group [${group.Hash || group.Name || '--N/A--'}] RecordManifest [${group.RecordManifest}] not found.`, { group });
							}
						}
					}
				}
				if (recordSetCount == 0 && !descriptorSectionKeys.has(section.Hash))
				{
					this.fable.log.info(`[WARN] Section [${section.Hash}] exists but no descriptor has that section.`);
				}
				if (Array.isArray(section.Solvers))
				{
					allSolvers[section.Hash] = [];
					for (const solver of section.Solvers)
					{
						allSolvers[section.Hash].push(solver);
					}
				}
			}
		}
		if (orphanedDeclarationDescriptorHashes.size)
		{
			for (const address of Array.from(orphanedDeclarationDescriptorHashes))
			{
				const descriptor = descriptorMap[address];
				const matcher = this.getEscapedRegex(descriptor.Hash);
				const dependentSections = Object.entries(allSolvers).filter(([ sectionHash, solvers ]) => solvers.some((s) => s.match(matcher))).map(([ sectionHash ]) => manifestInfo.data.Sections.find((s) => s.Hash === sectionHash));
				if (!dependentSections.length)
				{
					this.fable.log.info(`[WARN] Orphaned declaration descriptor; including at root: [${descriptor.DataAddress}](${descriptor.Hash})`);
					manifestSchema.Manifest.Descriptors[address] = descriptor;
				}
				else
				{
					orphanedDeclarationDescriptorHashes.delete(address);
					for (const section of dependentSections)
					{
						if (!includeBySection[section.Hash])
						{
							includeBySection[section.Hash] = {};
						}
						if (!includeBySection[section.Hash].Descriptors)
						{
							includeBySection[section.Hash].Descriptors = {};
						}
						includeBySection[section.Hash].Descriptors[address] = descriptor;
					}
				}
			}
		}
		for (const refManifestKey of Object.keys(referenceManifestMap))
		{
			if (!seenManifests.has(refManifestKey))
			{
				this.fable.log.info(`[ERROR] ReferenceManifest [${refManifestKey}] not used.`);
			}
		}

		// generate export
		if (orderedSectionHashes.length < 1)
		{
			this.fable.log.info(`[WARN] No sections found for [${manifestInfo.name}]; skipping.`);
			return problems;
		}
		comprehension.ManifestSchema.push(manifestSchema);
		let ordinal = 0;
		for (const sectionHash of orderedSectionHashes)
		{
			++ordinal;
			const sectionManifest =
			{
				Descriptors: includeBySection[sectionHash]?.Descriptors || {},
				Sections: [],
				ReferenceManifests: includeBySection[sectionHash]?.ReferenceManifests || {},
			};
			const section = manifestInfo.data.Sections.find((s) => s.Hash === sectionHash);
			if (section)
			{
				sectionManifest.Sections.push(section);
			}
			const manifestSchemaPartial =
			{
				GUIDManifestSchemaPartial: `${manifestSchema.GUIDManifestSchema}-${sectionHash}`,
				Hash: sectionHash,
				Type: 'Section',
				Title: `[${manifestSchema.Title}] ${section.Name || sectionHash}`,
				Version: 1,
				Description: '',
				ManifestPartial: sectionManifest,
			};
			comprehension.ManifestSchemaPartial.push(manifestSchemaPartial);
			const manifestSchemaManifestSchemaPartialJoin =
			{
				GUIDManifestSchemaManifestSchemaPartialJoin: `${manifestSchema.GUIDManifestSchema}-${sectionHash}`,
				GUIDManifestSchema: manifestSchema.GUIDManifestSchema,
				GUIDManifestSchemaPartial: manifestSchemaPartial.GUIDManifestSchemaPartial,
				Ordinal: ordinal,
			};
			comprehension.ManifestSchemaManifestSchemaPartialJoin.push(manifestSchemaManifestSchemaPartialJoin);
		}
		return problems;
	}

	/**
	 * @typedef {Object} ManifestInfo
	 * @property {string} name - the name of the manifest
	 * @property {string} path - the path to the manifest
	 * @property {any} data - the manifest data
	 */

	/**
	 * @param {Array<ManifestInfo>} manifestInfos - the manifest info
	 */
	async generateComprehension(manifestInfos, comprehensionOutputPath = 'comprehension.json')
	{
		const comprehension =
		{
			ManifestSchema: [],
			ManifestSchemaPartial: [],
			ManifestSchemaManifestSchemaPartialJoin: [],
		};
		for (const manifestInfo of manifestInfos)
		{
			if (this.addManifestToComprehension(manifestInfo, comprehension))
			{
				this.fable.log.info(`[WARN] Problems found in [${manifestInfo.name}].`);
			}
		}
		this.fable.log.info('Writing output...');
		Fs.writeFileSync(comprehensionOutputPath, JSON.stringify(comprehension, null, 2));
		return Path.resolve(comprehensionOutputPath);
	}

	/**
	 * Generate a RegExp to match a string, escaping any special characters.
	 *
	 * @param {string} str - the string to match
	 * @return {RegExp} the escaped regex
	 */
	getEscapedRegex(str)
	{
		return new RegExp(`\\b${str.replace(/[/\-\\^$*+?.()|[\]{}]/g, '\\$&')}\\b`);
	}

	/*
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		this.options.CommandKeyword = 'load_comprehension';
		this.options.Description = 'Load a comprehension into Headlight.';

		this.options.Aliases.push('load');

		this.options.CommandArguments.push({ Name: '<comprehension_file>', Description: 'The comprehension file path.' });

		this.addCommand();

		if (!('HeadlightRestClient' in this.fable.services))
		{
			this.fable.serviceManager.addServiceType('HeadlightRestClient', require(`headlight-rest-client`));
			if (!this.options.hasOwnProperty('HeadlightAPIURL'))
			{
				this.options.HeadlightAPIURL = 'https://api.qa.headlight.com/1.0/';
			}
			this.fable.serviceManager.instantiateServiceProvider('HeadlightRestClient', this.pict.ProgramConfiguration);
		}

		this.comprehension = {};
	}
	*/

	runAdapter(pAnticipate, pAdapter, pDataMap, fMarshalRecord)
	{
		let tmpAdapter = this.fable.servicesMap.IntegrationAdapter[pAdapter];
		let tmpMarshalRecordFunction = fMarshalRecord;
		if (!tmpAdapter)
		{
			throw new Error(`Adapter [${pAdapter}] not found.`);
		}
		if (!pDataMap)
		{
			this.log.info(`No records to push for [${pAdapter}].`);
			return false;
		}
		pAnticipate.anticipate(
			(fDone) =>
			{
				for (const tmpRecord in pDataMap)
				{
					tmpAdapter.addSourceRecord(pDataMap[tmpRecord]);
				}
				return fDone()
			});
		pAnticipate.anticipate(
			(fDone) =>
			{
				tmpAdapter.integrateRecords(fDone, tmpMarshalRecordFunction);
			});
	}

	getCapitalLettersAsString(inputString)
	{
		let tmpRegex = /[A-Z]/g;
		let tmpMatch = inputString.match(tmpRegex);
		let tmpString = tmpMatch ? tmpMatch.join('') : 'UNK';
		return tmpString;
	}

	pushComprehension(pComprehensionFilePath, fCallback)
	{
		let tmpAnticipate = this.fable.newAnticipate();
		this.fable.log.info(`Pushing comprehension file [${pComprehensionFilePath}] to the Headlight API.`);

		this.fable.log.info(`Initializing and configuring data integration adapters...`);
		this.fable.serviceManager.addServiceType('IntegrationAdapter', IntegrationAdapter);

		/*
		 * TODO: is this necessary?
		tmpAnticipate.anticipate(
			(fCallback) =>
			{
				this.fable.HeadlightRestClient.authenticate(
					(pError, pResponse) =>
					{
						if (pError)
						{
							this.log.error(`Error authenticating`, pError);
							return fCallback(typeof pError === 'string' ? new Error(pError) : pError);
						}
						this.log.info(`Authenticated - Session [${pResponse.SessionID}]`);
						return fCallback();
					});
			});
		 */

		tmpAnticipate.anticipate(
			(fCallback) =>
			{
				try
				{
					this.fable.log.info(`Loading Comprehension File...`);
					this.comprehension = require(pComprehensionFilePath);
					return fCallback();
				}
				catch(pError)
				{
					this.fable.log.error(`Error loading comprehension file [${pComprehensionFilePath}]: ${pError}`, pError);
					return fCallback(pError);
				}
			});

		tmpAnticipate.anticipate(
			(fCallback) =>
			{
				this.fable.log.info(`Wiring up Integration Adapters...`);

				let tmpIntegrationAdapterSet = Object.keys(this.comprehension);

				for (let i = 0; i < tmpIntegrationAdapterSet.length; i++)
				{
					// Initialize the adapter
					let tmpAdapterKey = tmpIntegrationAdapterSet[i];
					IntegrationAdapter.getAdapter(this.fable, tmpAdapterKey, this.getCapitalLettersAsString(tmpAdapterKey), { SimpleMarshal: true });
					this.runAdapter(tmpAnticipate, tmpAdapterKey, this.comprehension[tmpAdapterKey]);
				}

				return fCallback();
			});

		tmpAnticipate.wait(
			(pError) =>
			{
				if (pError)
				{
					this.fable.log.error(`Error importing comprehension file.`, pError);
					return fCallback(pError);
				}
				this.fable.log.info(`Finished importing comprehension file.`);
				return fCallback();
			});
	}
}

module.exports = ManifestPackagingService;
